/**
 * Represents-clients field module — modular-form replacement for the legacy
 * "Do you represent clients?" radio.
 *
 * Replaces:
 *  • Schema → packages/frontend/src/schema/workspaceSchema.ts:152-158
 *      representsClients: yup.string().when(['entityType'], ([entityType], schema) => {
 *        if (entityType === EntityTypeEnum.Individual) return schema.nullable();
 *        return schema.required('Please Select a Value to Proceed');
 *      }),
 *  • UI → BusinessDetailsForm.tsx:537-542 (<RadioButtons name="representsClients" .../>)
 *
 * Visibility is controlled by the parent — this field is never rendered for
 * Individual entity types. Held as a 'yes'|'no' string; converted to a
 * boolean in the submit mapper.
 */
import { EntityTypeEnum } from '@sr-sdks/api-sdk-axios';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
} from '@sr/ui-library';
import { Control } from 'react-hook-form';
import * as yup from 'yup';
import { YES_NO_OPTIONS } from '../../shared/defaults';
import { WORKSPACE_DETAILS_FORM_FIELDS } from '../../shared/fieldNames';
import { WorkspaceDetailsFormValues } from '../../shared/types';

interface RepresentsClientsFieldProps {
  control: Control<WorkspaceDetailsFormValues>;
}

export const RepresentsClientsField = ({
  control,
}: RepresentsClientsFieldProps) => (
  <FormField
    name={WORKSPACE_DETAILS_FORM_FIELDS.representsClients}
    control={control}
    render={({ field }) => (
      <FormItem>
        <FormLabel
          asterisk
          tooltipContent="Client representation refers to an agency representing a person or entity as part of a legal or contractual agreement. By answering 'Yes', you acknowledge that you are entitled to make claims, access payment statements, and share information with Screenrights on behalf of one or more clients."
        >
          Represents Clients
        </FormLabel>
        <FormControl>
          <Select
            allowDeselect={false}
            options={YES_NO_OPTIONS}
            placeholder="Select..."
            value={field.value ?? ''}
            onValueChange={field.onChange}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);

RepresentsClientsField.schema = yup
  .string()
  .when(
    ['businessNumberCountry', 'entityType'],
    ([businessNumberCountry, entityType], schema) => {
      if (entityType === EntityTypeEnum.Individual) return schema.nullable();
      // Hidden until a country and an entity type are chosen (legacy gate) —
      // a hidden field must not block submit.
      if (!businessNumberCountry || !entityType) return schema.nullable();

      return schema.required('Please select a value to proceed');
    },
  );
